import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [status, setStatus] = useState('checking');
  const [packageType, setPackageType] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await api.getPaymentStatus(sessionId);
        
        if (response.status === 'completed' || response.payment_status === 'paid') {
          setStatus('success');
          setPackageType(response.package_type || 'subscription');
          refreshProfile();
        } else if (response.session_status === 'expired') {
          setStatus('error');
        } else if (attempts < maxAttempts) {
          // Continue polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        if (attempts < maxAttempts) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [searchParams, attempts, refreshProfile]);

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full border-border/50 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-8 h-8 text-accent animate-spin" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Processing Payment</h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground mb-6">
                {packageType === 'subscription' 
                  ? "Welcome to ConnectMail! Your account is now active."
                  : "Your additional ad slot has been added to your account."
                }
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Payment Issue</h1>
              <p className="text-muted-foreground mb-6">
                There was an issue confirming your payment. If you believe this is an error, 
                please contact support.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
